import { useEffect, useState } from "react";
import {
  AlertTriangle,
  CheckCircle,
  Loader,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { staffAPI } from "../../services/AdminPortal/staffAPI";
import { taskAPI } from "../../services/AdminPortal/taskAPI";
import { MappingStyles } from "../../utils/utils";
import TaskUpdateDialog from "../../components/StaffPortal/TaskUpdateDialog";
import MetricCardsCM from "../../components/MetricCardsCM";

const DashboardTab = () => {
  const { user } = useAuth();
  const [staffWithLoansData, setStaffWithLoansData] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [dataError, setDataError] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [tasksLoading, setTasksLoading] = useState(false);
  const staffId = user?.staffId;

  // Fetch tasks
  const fetchTasks = async () => {
    if (!staffId) return;
    try {
      setTasksLoading(true);
      const response = await taskAPI.getStaffTasks(staffId);
      if (response.success) {
        setTasks(response.data.tasks || []);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setTasksLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    if (status === "COMPLETED") {
      return <CheckCircle size={16} className="text-green-500" />;
    } else if (status === "PENDING") {
      return (
        <div className="flex items-center gap-1 text-yellow-500">
          <AlertTriangle size={16} />
        </div>
      );
    } else if (status === "IN_PROGRESS") {
      return (
        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      );
    } else if (status === "CANCELLED") {
      return <div className="w-4 h-4 rounded-full bg-red-500"></div>;
    } else {
      return null;
    }
  };

  const getPriorityBgColor = (priority) => {
    switch (priority) {
      case "LOW":
        return "bg-green-400/20";
      case "MEDIUM":
        return "bg-yellow-400/20";
      case "HIGH":
        return "bg-red-300/20";
      case "URGENT":
        return "bg-red-600/20";
      default:
        return "bg-[#131313]";
    }
  };

  const handleTaskUpdate = async (taskId, updateData) => {
    try {
      const response = await taskAPI.updateTaskStatus(taskId, updateData);
      if (response.success) {
        await fetchTasks();
        setShowTaskDialog(false);
        setSelectedTask(null);
      }
    } catch (error) {
      console.error("Error updating task:", error);
      throw error;
    }
  };

  const openTaskDialog = (task) => {
    setSelectedTask(task);
    setShowTaskDialog(true);
  };

  // Data Fetching - same as StaffDashboard
  const fetchStaffData = async () => {
    if (!staffId) {
      setLoadingData(false);
      return;
    }
    try {
      setLoadingData(true);

      // 1. Fetch detailed staff information
      const staffDetailsResponse = await staffAPI.getStaffById({ staffId });

      if (staffDetailsResponse.success && staffDetailsResponse.data) {
        setStaffWithLoansData({
          staffDetails: staffDetailsResponse.data,
        });
        setDataError(null);
      } else {
        setDataError(
          staffDetailsResponse.message || "Failed to fetch staff details."
        );
      }
    } catch (error) {
      console.error("Error fetching staff data:", error);
      setDataError(error.message || "Failed to fetch staff data");
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    fetchStaffData();
    fetchTasks();
  }, [staffId]);

  // Mapping for staff roles
  const roleMapping = {
    admin: "Admin",
    loanOfficer: "Loan Officer",
    subAdmin: "Sub Admin",
    hr: "HR",
    "credit-manager": "Credit Manager",
    marketing: "Marketing",
    sales: "Sales",
    strategy: "Strategy",
  };

  // Loading and Error states rendering
  if (loadingData) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (dataError) {
    return (
      <div className="min-h-screen bg-black text-red-500 p-4">
        Error: {dataError}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Staff Profile Section */}
      {staffWithLoansData?.staffDetails && (
        <div className="bg-[#131313] rounded-lg p-3 mx-2 my-4 md:p-4 md:m-4">
          <div>
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-600 flex items-center justify-center text-white text-lg md:text-xl font-bold overflow-hidden flex-shrink-0">
                  <img
                    src={
                      staffWithLoansData.staffDetails.profileImage ||
                      `https://ui-avatars.com/api/?name=${staffWithLoansData.staffDetails.name}&background=fdba74&color=854d0e&size=128`
                    }
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg md:text-xl font-bold capitalize truncate">
                    {staffWithLoansData.staffDetails.name}
                  </h2>
                  <div className="text-xs md:text-sm text-gray-400">
                    STAFF ID
                    {staffWithLoansData.staffDetails.staffId ||
                      staffWithLoansData.staffDetails._id}
                  </div>
                </div>
              </div>
            </div>
            {/* Staff Details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              <div>
                <div className="text-xs text-gray-400">Role</div>
                <div className="text-sm">
                  {roleMapping[staffWithLoansData.staffDetails.role] ||
                    staffWithLoansData.staffDetails.role}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-400">Contact Number</div>
                <div className="text-sm">
                  {staffWithLoansData.staffDetails.phone ||
                    staffWithLoansData.staffDetails.phoneNumber}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-400">Email ID</div>
                <div className="text-sm truncate">
                  {staffWithLoansData.staffDetails.email}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-400">Date of Joining</div>
                <div className="text-sm">
                  {staffWithLoansData.staffDetails.createdAt
                    ? new Date(
                        staffWithLoansData.staffDetails.createdAt
                      ).toLocaleDateString("en-GB")
                    : "N/A"}
                </div>
              </div>

              {/* ✅ Branch Details */}
              {/* <div>
                <div className="text-xs text-gray-400">Branch Name</div>
                <div className="text-sm">
                  {staffWithLoansData.staffDetails.branchName || "N/A"}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-400">Branch Type</div>
                <div className="text-sm">
                  {staffWithLoansData.staffDetails.branchType === "district"
                    ? "District Level"
                    : staffWithLoansData.staffDetails.branchType === "mandal"
                    ? "Mandal Level"
                    : staffWithLoansData.staffDetails.branchType === "village"
                    ? "Village Level"
                    : staffWithLoansData.staffDetails.branchType || "N/A"}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-400">Branch Location</div>
                <div className="text-sm">
                  {staffWithLoansData.staffDetails.branchLocation || "N/A"}
                </div>
              </div> */}
            </div>
          </div>
        </div>
      )}

      {/* Metrics Cards Section */}
      <div className="mx-2 my-4 md:mx-4">
        <MetricCardsCM />
      </div>

      <main className="p-4">
        {/* Tasks Section */}
        <div className="bg-[#131313] rounded-lg p-4 border border-[#333333]">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-medium">MY TASKS</h3>
            <div className="text-xs text-gray-400">
              {tasks.length} task{tasks.length !== 1 ? "s" : ""}
            </div>
          </div>

          {/* Tasks list */}
          <div className="max-h-[70vh] overflow-y-auto space-y-3">
            {tasksLoading ? (
              <div className="text-center text-gray-500 py-6">
                <Loader className="animate-spin mx-auto mb-2" size={20} />
                Loading tasks...
              </div>
            ) : tasks.length > 0 ? (
              tasks.map((task) => (
                <div
                  key={task._id}
                  className={`flex items-center justify-between p-3 hover:bg-[#131313] rounded transition cursor-pointer ${getPriorityBgColor(
                    task.priority
                  )}`}
                  onClick={() => openTaskDialog(task)}
                >
                  <div className="flex items-center gap-3">
                    {/* Status Icon */}
                    <div className="flex-shrink-0">
                      {getStatusIcon(task.status)}
                    </div>

                    {/* Task Info */}
                    <div>
                      <p className="text-sm text-white font-medium">
                        {task.title}
                      </p>
                      <p className="text-xs text-gray-400">{task.priority}</p>
                    </div>
                  </div>

                  {/* Status and Update Button */}
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-2 py-1 rounded text-xs ${MappingStyles(
                        task.status
                      )}`}
                    >
                      {task.status.replace("_", " ")}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openTaskDialog(task);
                      }}
                      className="text-yellow-500 text-xs hover:text-yellow-400"
                    >
                      Update Status
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-6 text-sm">
                No tasks assigned yet
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Task Update Dialog */}
      <TaskUpdateDialog
        isOpen={showTaskDialog}
        onClose={() => {
          setShowTaskDialog(false);
          setSelectedTask(null);
        }}
        task={selectedTask}
        onUpdate={handleTaskUpdate}
      />
    </div>
  );
};

export default DashboardTab;